import { Button, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, ChevronLeft } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { apiFetch } from '../../lib/apiFetch.js';
import { downloadCredentialed, openCredentialed } from '../../lib/download.js';
import { isRichTextEmpty, sanitizeRichTextHtml } from '../../lib/richTextHtml.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { type MoneyFormatters, useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { AddPaymentForm } from './components/AddPaymentForm.js';
import { CreateJobFromInvoice } from './components/CreateJobFromInvoice.js';
import { InvoiceTotalsGrid } from './components/InvoiceTotalsGrid.js';
import { PaymentsTable } from './components/PaymentsTable.js';
import type { InvoiceDetail, InvoicePayment } from './contractsMirror.js';
import { invoiceAttachmentUrl, invoicePdfUrl, paymentAttachmentUrl } from './exportLinks.js';
import {
  invoiceQueryKey,
  useDeleteInvoiceAttachmentMutation,
  useUploadInvoiceAttachmentMutation,
} from './useInvoiceQuery.js';

/**
 * Invoice-detail screen (legacy `InvoiceDetailPage`) on the locked brand,
 * uplifted to the P22 redesign grammar (D-04 re-skin — functionality UNCHANGED).
 * Header + actions row (Download PDF / Back), archived banner, the 4-card totals
 * grid with the refreshed status pill, Notes + Attachment cards, the Payments
 * table with a token-styled destructive delete `ConfirmDialog` (focus-trapped,
 * Esc cancels — A11Y-01), and **add-payment now lives in a focus-trapped drawer**
 * (UI-SPEC §4 "Drawers / dialogs over page nav") instead of an always-open form.
 *
 * P21 CONTRACTS PRESERVED (no change this re-skin):
 * - **Read-only GET**: this view never mutates on load; the server derives status
 *   read-only and `useInvoiceQuery` keeps the default staleTime so a refetch picks
 *   up the latest server value (the project's GET-mutate footgun is avoided).
 * - **Exports via api-host GET**: "Download PDF" links to `invoicePdfUrl` (engine
 *   reused, D-06 — never a client renderer).
 * - **Public pay stays SSR**: the customer pay flow is never re-implemented here.
 *
 * ACTIONS (21-11): the api-host now exposes the invoice write surface, so the
 * header carries Edit / Send-for-signature / Archive-Restore actions and the
 * payment delete is a real `DELETE /api/v1/invoices/:id/payments/:paymentId` that
 * recomputes totals + status server-side (the SPA invalidates the detail query on
 * success). Send-for-signature surfaces the returned tenant-host public sign link.
 */

export interface InvoiceDetailViewProps {
  invoice: InvoiceDetail;
}

export function InvoiceDetailView({ invoice }: InvoiceDetailViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [paymentToDelete, setPaymentToDelete] = useState<InvoicePayment | null>(null);
  const [deletePaymentPending, setDeletePaymentPending] = useState(false);
  const [deletePaymentError, setDeletePaymentError] = useState<string | null>(null);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [attachmentDeleteOpen, setAttachmentDeleteOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [issueConfirmOpen, setIssueConfirmOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<{ publicUrl: string } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // D-03 check-before-send: the recipient + preview dialog opened by "Send for
  // signature" before anything actually goes out (the prod one-click gap).
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendRecipientEmail, setSendRecipientEmail] = useState('');
  const attachmentInputId = useId();

  const uploadAttachment = useUploadInvoiceAttachmentMutation(invoice.id);
  const deleteAttachment = useDeleteInvoiceAttachmentMutation(invoice.id);

  const archived = Boolean(invoice.archivedAt);
  const payments = invoice.payments ?? [];

  const onSelectAttachment = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file re-triggers change.
    event.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.set('attachment', file);
    uploadAttachment.mutate(formData);
  };

  const confirmDeleteAttachment = () => {
    setAttachmentDeleteOpen(false);
    deleteAttachment.mutate();
  };

  const invalidateDetail = () =>
    queryClient.invalidateQueries({ queryKey: invoiceQueryKey(invoice.id) });

  // Real DELETE of a recorded payment (21-11). On the `{ ok }` envelope the server
  // has recomputed totalPaid + status; invalidating the detail query refreshes the
  // totals grid, status pill, and payments table. An `{ error }` (e.g. an archived
  // invoice) is surfaced instead of being silently swallowed.
  const confirmDeletePayment = async () => {
    if (!paymentToDelete) return;
    setDeletePaymentPending(true);
    setDeletePaymentError(null);
    try {
      const response = await apiFetch(
        `/api/v1/invoices/${invoice.id}/payments/${paymentToDelete.id}`,
        { method: 'DELETE' },
      );
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
      } | null;
      if (!response.ok || !result || result.ok === false) {
        setDeletePaymentError(result?.error ?? t('invoices:deletePayment.error'));
        return;
      }
      setPaymentToDelete(null);
      await invalidateDetail();
    } catch {
      setDeletePaymentError(t('invoices:deletePayment.error'));
    } finally {
      setDeletePaymentPending(false);
    }
  };

  // D-03: open the check-before-send dialog instead of firing immediately —
  // prefill the recipient from the saved customer email so the common case is
  // a single confirm, but the operator can edit it before anything sends.
  const openSendDialog = () => {
    setActionError(null);
    setSendRecipientEmail(invoice.customerEmail ?? '');
    setSendDialogOpen(true);
  };

  // Send the invoice for signature (21-11, override recipient added 27.1-02):
  // the server emails the customer a public sign link (on the tenant host) and
  // returns it; show + copy it here. `recipientEmail` is the operator-edited
  // value from the check-before-send dialog — it overrides the saved customer
  // email for THIS send only (the invoice record is never mutated).
  const sendForSignature = async (recipientEmail: string) => {
    setSendDialogOpen(false);
    setActionPending(true);
    setActionError(null);
    setLinkCopied(false);
    try {
      const response = await apiFetch(`/api/v1/invoices/${invoice.id}/send-for-signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipientEmail }),
      });
      const result = (await response.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        publicUrl?: string;
      } | null;
      if (!response.ok || !result || result.ok === false) {
        setActionError(result?.error ?? t('invoices:sendResult.error'));
        return;
      }
      setSendResult({ publicUrl: result.publicUrl ?? '' });
      await invalidateDetail();
    } catch {
      setActionError(t('invoices:sendResult.error'));
    } finally {
      setActionPending(false);
    }
  };

  const runArchive = async () => {
    setArchiveConfirmOpen(false);
    setActionPending(true);
    setActionError(null);
    try {
      const response = await apiFetch(`/api/v1/invoices/${invoice.id}/archive`, { method: 'POST' });
      const result = (await response.json().catch(() => null)) as { ok?: boolean } | null;
      if (!response.ok || !result || result.ok === false) {
        setActionError(t('invoices:actionError'));
        return;
      }
      void navigate({ to: '/invoices' });
    } catch {
      setActionError(t('invoices:actionError'));
    } finally {
      setActionPending(false);
    }
  };

  const runRestore = async () => {
    setRestoreConfirmOpen(false);
    setActionPending(true);
    setActionError(null);
    try {
      const response = await apiFetch(`/api/v1/invoices/${invoice.id}/restore`, { method: 'POST' });
      const result = (await response.json().catch(() => null)) as { ok?: boolean } | null;
      if (!response.ok || !result || result.ok === false) {
        setActionError(t('invoices:actionError'));
        return;
      }
      await invalidateDetail();
    } catch {
      setActionError(t('invoices:actionError'));
    } finally {
      setActionPending(false);
    }
  };

  // Issue a draft WITHOUT emailing anybody — the hand-delivered and printed case.
  //
  // "Invoice balance" on the job screen mints a Draft, and a draft is deliberately kept out of the
  // job's apply-to-invoice picker (money must not land on a document the customer has never seen).
  // Sending was the only way across that line, and it refuses without a customer email — so an
  // operator who hands over a printed bill could never record the payment against it. This is that
  // missing step; it does not send, render, or create a customer link.
  const runIssue = async () => {
    setIssueConfirmOpen(false);
    setActionPending(true);
    setActionError(null);
    try {
      const response = await apiFetch(`/api/v1/invoices/${invoice.id}/issue`, { method: 'POST' });
      const result = (await response.json().catch(() => null)) as { ok?: boolean } | null;
      if (!response.ok || !result || result.ok === false) {
        setActionError(t('invoices:actionError'));
        return;
      }
      // The server owns the resulting status (and answers a second press with the status it found
      // rather than an error), so refetch rather than assuming 'Sent' locally.
      await invalidateDetail();
    } catch {
      setActionError(t('invoices:actionError'));
    } finally {
      setActionPending(false);
    }
  };

  const copySignLink = async () => {
    if (!sendResult?.publicUrl) return;
    try {
      await navigator.clipboard.writeText(sendResult.publicUrl);
      setLinkCopied(true);
    } catch {
      setLinkCopied(false);
    }
  };

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <Link
          to="/invoices"
          className="mb-3 inline-flex min-h-11 items-center gap-1 rounded-md pr-3 font-medium text-muted-foreground text-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t('invoices:actions.backToInvoices')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-semibold text-3xl text-foreground">
              {t('invoices:detail.heading', {
                number: invoice.invoiceNumber || `#${invoice.id}`,
              })}
            </h1>
            {invoice.jobName ? (
              <p className="mt-1 text-muted-foreground text-sm">{invoice.jobName}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!archived ? (
              <>
                {invoice.editLocked ? (
                  <p className="text-muted-foreground text-sm">{t('invoices:detail.editLocked')}</p>
                ) : (
                  <Button asChild variant="secondary">
                    <Link to="/invoices/$id/edit" params={{ id: String(invoice.id) }}>
                      {t('invoices:actions.edit')}
                    </Link>
                  </Button>
                )}
                {/* Renders nothing once the invoice has a job — see the component. */}
                <CreateJobFromInvoice invoice={invoice} />
                {/* Only on a draft: it is the one status the action can move, and offering it on an
                  already-issued invoice would suggest pressing it does something. */}
                {invoice.status === 'Draft' ? (
                  <Button
                    variant="secondary"
                    disabled={actionPending}
                    onClick={() => setIssueConfirmOpen(true)}
                  >
                    {t('invoices:actions.markIssued')}
                  </Button>
                ) : null}
                <Button variant="secondary" disabled={actionPending} onClick={openSendDialog}>
                  {actionPending
                    ? t('invoices:actions.sending')
                    : t('invoices:actions.sendForSignature')}
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                disabled={actionPending}
                onClick={() => setRestoreConfirmOpen(true)}
              >
                {t('invoices:actions.restore')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() =>
                void downloadCredentialed(
                  invoicePdfUrl(invoice.id),
                  `invoice-${invoice.invoiceNumber || invoice.id}.pdf`,
                ).catch((err) => console.error('invoice pdf download failed', err))
              }
            >
              {t('invoices:actions.downloadPdf')}
            </Button>
            {!archived ? (
              <Button
                variant="outline"
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                disabled={actionPending}
                onClick={() => setArchiveConfirmOpen(true)}
              >
                {t('invoices:actions.archive')}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      {actionError ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm"
        >
          {actionError}
        </div>
      ) : null}

      {sendResult ? (
        <div className="mb-4 rounded-md border border-border bg-card p-4">
          <h2 className="font-semibold text-foreground text-sm">
            {t('invoices:sendResult.heading')}
          </h2>
          <p className="mt-1 text-muted-foreground text-sm">{t('invoices:sendResult.body')}</p>
          {sendResult.publicUrl ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="rounded bg-muted px-2 py-1 font-mono text-foreground text-xs">
                {sendResult.publicUrl}
              </code>
              <Button variant="ghost" size="sm" onClick={() => void copySignLink()}>
                {linkCopied ? t('invoices:sendResult.copied') : t('invoices:sendResult.copyLink')}
              </Button>
              <a
                href={sendResult.publicUrl}
                target="_blank"
                rel="noreferrer"
                className="text-primary-ink text-sm underline"
              >
                {t('invoices:sendResult.open')}
              </a>
            </div>
          ) : null}
        </div>
      ) : null}

      {archived ? (
        <div className="mb-4 rounded-md border border-(--chart-4)/30 bg-(--chart-4)/10 p-4 text-warning text-sm">
          {t('invoices:detail.archivedBanner')}
        </div>
      ) : null}

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-3 font-semibold text-foreground text-xl">
            {t('invoices:detail.summaryHeading')}
          </h2>
          <dl className="grid gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.invoiceNumber')}</dt>
              <dd className="font-mono text-foreground">
                {invoice.invoiceNumber || t('invoices:detail.noneDash')}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.dateIssued')}</dt>
              <dd className="font-mono text-foreground">
                {formatUiDate(invoice.dateIssued, locale, { dateStyle: 'medium' })}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.dueDate')}</dt>
              <dd className="font-mono text-foreground">
                {formatUiDate(invoice.dueDate, locale, { dateStyle: 'medium' })}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.client')}</dt>
              <dd className="text-foreground">
                {invoice.clientName || t('invoices:detail.noneDash')}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.job')}</dt>
              <dd className="text-foreground">
                {invoice.jobName || t('invoices:detail.noneDash')}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-3 font-semibold text-foreground text-xl">
            {t('invoices:detail.attachmentHeading')}
          </h2>
          {invoice.attachmentFilename ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <CheckCircle2 className="size-4 text-success" aria-hidden="true" />
              <span className="text-foreground">{invoice.attachmentFilename}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  void openCredentialed(invoiceAttachmentUrl(invoice.id)).catch((err) =>
                    console.error('invoice attachment view failed', err),
                  )
                }
              >
                {t('invoices:detail.viewAttachment')}
              </Button>
              {!archived ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={deleteAttachment.isPending}
                  onClick={() => setAttachmentDeleteOpen(true)}
                >
                  {t('invoices:detail.deleteAttachment')}
                </Button>
              ) : null}
            </div>
          ) : archived ? (
            <p className="text-muted-foreground text-sm">{t('invoices:detail.noAttachment')}</p>
          ) : (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label htmlFor={attachmentInputId} className="font-semibold text-foreground text-sm">
                {t('invoices:detail.attachmentLabel')}
              </label>
              <input
                id={attachmentInputId}
                type="file"
                onChange={onSelectAttachment}
                disabled={uploadAttachment.isPending}
                className="text-foreground text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:font-medium file:text-secondary-foreground file:text-sm"
              />
            </div>
          )}
          {uploadAttachment.isError || deleteAttachment.isError ? (
            <p role="alert" className="mt-2 text-destructive text-sm">
              {t('invoices:detail.attachmentError')}
            </p>
          ) : null}
          <h3 className="mt-4 mb-2 font-semibold text-foreground text-sm">
            {t('invoices:detail.notesHeading')}
          </h3>
          {/* `notes` is a rich-HTML document field. It is RENDERED here rather than flattened, so
              the pane matches what the customer receives on the PDF — the builder now writes this
              field with a real editor. Every render goes through sanitizeRichTextHtml, whose
              allowlist mirrors the api worker's sanitizeRichHtml; see lib/richTextHtml.ts. */}
          {isRichTextEmpty(invoice.notes) ? (
            <p className="text-muted-foreground text-sm">{t('invoices:detail.noneDash')}</p>
          ) : (
            <div
              className="rich-text text-muted-foreground text-sm"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized on the line above
              dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(invoice.notes) }}
            />
          )}
        </div>
      </section>

      <InvoiceTotalsGrid
        amount={invoice.amount}
        paid={invoice.paid}
        outstanding={invoice.outstanding}
        status={invoice.status}
        overdue={invoice.overdue}
        archived={archived}
        fmt={fmt}
      />

      <section className="mt-4 rounded-md border border-border bg-card p-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold text-foreground text-xl">
            {t('invoices:payments.heading')}
          </h2>
          {!archived ? (
            <Button onClick={() => setAddPaymentOpen(true)}>
              {t('invoices:payments.addHeading')}
            </Button>
          ) : null}
        </div>
        <PaymentsTable
          payments={payments}
          fmt={fmt}
          renderAction={(payment) => (
            <div className="flex items-center justify-end gap-1">
              {payment.attachmentFilename ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    void openCredentialed(paymentAttachmentUrl(payment.id)).catch((err) =>
                      console.error('payment attachment view failed', err),
                    )
                  }
                >
                  {t('invoices:payments.view')}
                </Button>
              ) : null}
              {archived ? (
                <span className="text-muted-foreground text-xs">
                  {t('invoices:payments.locked')}
                </span>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setPaymentToDelete(payment)}>
                  {t('invoices:payments.delete')}
                </Button>
              )}
            </div>
          )}
        />
      </section>

      {/* Add-payment as a focus-trapped drawer (UI-SPEC §4 "Drawers over page nav").
          The form posts through apiFetch and invalidates the detail query on success,
          so the totals + status pill + payments table refresh underneath. */}
      <Drawer
        open={addPaymentOpen && !archived}
        title={t('invoices:payments.addHeading')}
        closeLabel={t('invoices:actions.cancel')}
        onClose={() => setAddPaymentOpen(false)}
      >
        <AddPaymentForm invoiceId={invoice.id} />
      </Drawer>

      <ConfirmDialog
        open={paymentToDelete !== null}
        heading={t('invoices:deletePayment.heading')}
        body={deletePaymentError ?? t('invoices:deletePayment.body')}
        confirmLabel={t('invoices:deletePayment.confirm')}
        cancelLabel={t('invoices:deletePayment.cancel')}
        confirmPending={deletePaymentPending}
        onConfirm={() => void confirmDeletePayment()}
        onCancel={() => {
          setPaymentToDelete(null);
          setDeletePaymentError(null);
        }}
      />

      <ConfirmDialog
        open={archiveConfirmOpen}
        heading={t('invoices:archiveDialog.heading')}
        body={t('invoices:archiveDialog.body')}
        confirmLabel={t('invoices:archiveDialog.confirm')}
        cancelLabel={t('invoices:archiveDialog.cancel')}
        confirmPending={actionPending}
        onConfirm={() => void runArchive()}
        onCancel={() => setArchiveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={issueConfirmOpen}
        heading={t('invoices:issueDialog.heading')}
        body={t('invoices:issueDialog.body')}
        confirmLabel={t('invoices:issueDialog.confirm')}
        cancelLabel={t('invoices:issueDialog.cancel')}
        confirmPending={actionPending}
        onConfirm={() => void runIssue()}
        onCancel={() => setIssueConfirmOpen(false)}
      />

      <ConfirmDialog
        open={restoreConfirmOpen}
        heading={t('invoices:restoreDialog.heading')}
        body={t('invoices:restoreDialog.body')}
        confirmLabel={t('invoices:restoreDialog.confirm')}
        cancelLabel={t('invoices:restoreDialog.cancel')}
        confirmPending={actionPending}
        onConfirm={() => void runRestore()}
        onCancel={() => setRestoreConfirmOpen(false)}
      />

      <ConfirmDialog
        open={attachmentDeleteOpen}
        heading={t('invoices:detail.deleteAttachmentHeading')}
        body={t('invoices:detail.deleteAttachmentBody')}
        confirmLabel={t('invoices:detail.deleteAttachmentConfirm')}
        cancelLabel={t('invoices:detail.deleteAttachmentCancel')}
        confirmPending={deleteAttachment.isPending}
        onConfirm={confirmDeleteAttachment}
        onCancel={() => setAttachmentDeleteOpen(false)}
      />

      <SendInvoiceDialog
        open={sendDialogOpen}
        invoice={invoice}
        recipientEmail={sendRecipientEmail}
        onRecipientEmailChange={setSendRecipientEmail}
        onCancel={() => setSendDialogOpen(false)}
        onConfirm={() => void sendForSignature(sendRecipientEmail.trim())}
        confirmPending={actionPending}
        fmt={fmt}
        locale={locale}
      />
    </div>
  );
}

const DRAWER_FOCUSABLE =
  'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

/**
 * A token-only, focus-trapped side drawer (UI-SPEC §4 + Accessibility Contract:
 * `aria-labelledby` heading, Esc cancels, focus returns on close, fully
 * keyboard-operable). Built with NO new dependency — the same focus-trap shape as
 * `_money/ConfirmDialog`, in a right-edge sheet. Used here to host the add-payment
 * form without a route change. Every color is a locked token (BRAND-06).
 */
function Drawer({
  open,
  title,
  closeLabel,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  closeLabel: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = panelRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = panelRef.current;
    node?.querySelector<HTMLElement>(DRAWER_FOCUSABLE)?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-foreground/40">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={handleKeyDown}
        className="flex h-full w-full max-w-md flex-col overflow-y-auto border-border border-l bg-card shadow-lg"
      >
        <div className="flex items-center justify-between gap-3 border-border border-b p-4">
          <h2 id={titleId} className="font-display font-semibold text-foreground text-xl">
            {title}
          </h2>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {closeLabel}
          </Button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// Simple client-side shape check — mirrors the server's minimal validation
// (contains '@', no CRLF/whitespace, length capped) plus a domain-dot sanity
// check for a better UX. The server (plan 27.1-02) is the source of truth and
// revalidates regardless (T-27.1-03) — this only gates the Confirm button.
const RECIPIENT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENT_EMAIL_LENGTH = 254;

function isValidRecipientEmail(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= MAX_RECIPIENT_EMAIL_LENGTH &&
    RECIPIENT_EMAIL_PATTERN.test(value)
  );
}

/**
 * D-03 check-before-send dialog (GAP-d): the send button no longer fires
 * immediately. It opens this token-styled, focus-trapped dialog that shows the
 * editable recipient (prefilled from `invoice.customerEmail`, empty when none)
 * and the facts the real email carries (invoice number, due date, and the amount
 * still owed, which is what the email asks for). It does not imitate the email's
 * subject or button: those are written by the server and vary with the invoice,
 * so a copy here would drift. Text only, no `dangerouslySetInnerHTML`
 * (T-27.1-06). Deliberately carries NO "team notified" claim anywhere
 * (T-27.1-07) — only the real public sign link (rendered by the existing
 * `sendResult` panel) is ever shown as confirmation.
 */
export function SendInvoiceDialog({
  open,
  invoice,
  recipientEmail,
  onRecipientEmailChange,
  onCancel,
  onConfirm,
  confirmPending,
  fmt,
  locale,
}: {
  open: boolean;
  invoice: InvoiceDetail;
  recipientEmail: string;
  onRecipientEmailChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  confirmPending: boolean;
  fmt: MoneyFormatters;
  locale: string;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const emailId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const trimmedEmail = recipientEmail.trim();
  const emailValid = isValidRecipientEmail(trimmedEmail);
  const showEmailError = trimmedEmail.length > 0 && !emailValid;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusable = Array.from(node.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const node = dialogRef.current;
    node?.querySelector<HTMLElement>(DRAWER_FOCUSABLE)?.focus();
    return () => {
      previouslyFocused.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        onKeyDown={handleKeyDown}
        className="w-full max-w-lg rounded-md border border-border bg-card p-6 shadow-lg"
      >
        <h2 id={headingId} className="font-display font-semibold text-foreground text-xl">
          {t('invoices:sendDialog.heading')}
        </h2>
        <p className="mt-1 text-muted-foreground text-sm">{t('invoices:sendDialog.body')}</p>

        <div className="mt-4 flex flex-col gap-1">
          <label htmlFor={emailId} className="font-medium text-foreground text-sm">
            {t('invoices:sendDialog.recipientLabel')}
          </label>
          <input
            id={emailId}
            type="email"
            value={recipientEmail}
            onChange={(event) => onRecipientEmailChange(event.target.value)}
            aria-invalid={showEmailError}
            required
            className="h-11 rounded-md border border-input bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-[invalid=true]:border-destructive"
          />
          {showEmailError ? (
            <p role="alert" className="text-destructive text-xs">
              {t('invoices:sendDialog.recipientInvalid')}
            </p>
          ) : null}
        </div>

        {/* Message preview — text-only rendering of the same fields the real
            email uses (auto-escaped by React; never raw server HTML). */}
        <div className="mt-4 rounded-md border border-border bg-muted/40 p-4">
          <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {t('invoices:sendDialog.previewLabel')}
          </p>
          <dl className="mt-3 grid gap-1 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.invoiceNumber')}</dt>
              <dd className="font-mono text-foreground">
                {invoice.invoiceNumber || t('invoices:detail.noneDash')}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:detail.dueDate')}</dt>
              <dd className="font-mono text-foreground">
                {formatUiDate(invoice.dueDate, locale, { dateStyle: 'medium' })}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">{t('invoices:sendDialog.amountDue')}</dt>
              <dd className="font-mono font-semibold text-foreground">
                {fmt.formatCurrency(invoice.outstanding)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            {t('invoices:actions.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={confirmPending || !emailValid}>
            {confirmPending ? t('invoices:actions.sending') : t('invoices:sendDialog.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
